import React, { FC, useEffect } from 'react'

import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { ScheduleSettingsHeader } from 'src/Components/ScheduleSettingsHeader'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import {
  getIsIdleTikTokCampaign,
  getIsLoadingTikTokCampaign,
  getScheduledTikTokCampaign,
} from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import {
  CampaignPlatformTypeEnum,
  TikTokCampaignPricingModelEnum,
} from 'src/Types/campaigns'
import { updateTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/api-actions'
import { ScheduleSettingsPageSkeleton } from 'src/Pages/Artist/ScheduleSettingsPageSkeleton'
import { useReels } from 'src/Hooks/useReels'
import { getIsNewUserForVideosBased } from 'src/Redux/auth-process/userSlice/selectors'

import { TiktokScheduleBudget } from './TiktokScheduleBudget.tsx'
import { TiktokScheduleSettingsModals } from './TiktokScheduleSettingsModals'
import { TiktokScheduleSteps } from './TiktokScheduleSteps'
import { TiktokScheduleParams } from './TiktokScheduleParams'
import { useTikTokScheduleStartDate } from './useTikTokScheduleStartDate'

const Container = styled.div`
  display: flex;
  max-width: 1120px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  @media screen and (min-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    align-self: stretch;
  }
`

const getArtistName = (
  scheduledCampaign: ReturnType<typeof getScheduledTikTokCampaign>,
): string =>
  scheduledCampaign.track?.authorName ||
  scheduledCampaign.trackAuthorName ||
  'No data'

const getTrackName = (
  scheduledCampaign: ReturnType<typeof getScheduledTikTokCampaign>,
): string =>
  scheduledCampaign.track?.title || scheduledCampaign.trackTitle || 'No data'

export const TiktokScheduleSettingsPage: FC = () => {
  const { number } = useParams()
  const { isReelsCampaign } = useReels()
  const dispatch = useAppDispatch()
  const scheduledCampaign = useAppSelector(getScheduledTikTokCampaign)
  const isNewUserForVideosBased = useAppSelector(getIsNewUserForVideosBased)
  const showVariants =
    (!scheduledCampaign.pricingModel || !scheduledCampaign.comment) &&
    !isReelsCampaign

  const isLoading = useAppSelector(getIsLoadingTikTokCampaign)
  const isIdle = useAppSelector(getIsIdleTikTokCampaign)
  const loading = isLoading || isIdle

  const campaignId = Number(scheduledCampaign.id)
  const { pricingModel } = scheduledCampaign

  useEffect(() => {
    if (
      isNewUserForVideosBased &&
      !pricingModel &&
      campaignId &&
      !isReelsCampaign
    ) {
      void dispatch(
        updateTikTokCampaign({
          id: campaignId,
          pricingModel: TikTokCampaignPricingModelEnum.ViewsBased,
        }),
      )
    }
  }, [
    isNewUserForVideosBased,
    pricingModel,
    campaignId,
    isReelsCampaign,
    dispatch,
  ])

  const trackCover =
    scheduledCampaign.track?.coverUrl ?? scheduledCampaign.trackCoverUrl

  const artistName = getArtistName(scheduledCampaign)

  const trackName = getTrackName(scheduledCampaign)

  const { startDate, todayEnable, onChangeDate } = useTikTokScheduleStartDate(
    Number(number),
  )

  if (loading) {
    return <ScheduleSettingsPageSkeleton />
  }
  return (
    <>
      <TiktokScheduleSettingsModals />
      <Container>
        <ScheduleSettingsHeader
          artistName={artistName}
          trackCover={trackCover}
          trackName={trackName}
          platform={
            isReelsCampaign
              ? CampaignPlatformTypeEnum.REELS
              : CampaignPlatformTypeEnum.TIKTOK
          }
          startDate={startDate}
          todayEnable={todayEnable}
          onChangeDate={onChangeDate}
          isEditable={true}
        />
        {!showVariants && (
          <Wrapper>
            <TiktokScheduleParams />
            <TiktokScheduleBudget />
          </Wrapper>
        )}
        {showVariants && (
          <Wrapper>
            <TiktokScheduleParams />
            <TiktokScheduleSteps />
          </Wrapper>
        )}
      </Container>
    </>
  )
}
